import { Schedule } from './store-schedule/stores-schedule';
import { StoreSubscription } from './store-subscriptions/subscriptions';
import { Tenant } from '../store-platform/platform';
import { ReferenceType } from '../public/store/types/IntegrationPayment';
import { Currency } from '../api/types/Currency';
import { PaymentMethod } from '../public/store/types/PaymentMethod';
import { Nutrient } from './store-catalog/catalog.constants';
import { CustomerAddress } from '../public/store/types/CustomerSocialLogin';
import { ItemTagDisplay } from '../shared/item-tag-row/item-tag-row.component';

/**
 * Criteria for the stores search endpoint.
 *
 * `id` and `alias` are mutually exclusive by design: the Stores List offers a
 * separate input for each and clears one when the other is used, so a search is
 * always by exactly one of them. `id` is an exact match, `alias` a contains match.
 */
export interface StoreSearchCriteria {
  id?: number;
  alias?: string;
  viewType?: string;
  currentStoreId?: number;
  deletedState?: string;
}

export interface ClientStore {
  id: number;
  name: string;
  companyName: string;
  vatNumber: string;
  description: string;
  coordinates: {
    longitude: number;
    latitude: number;
  };
  aliasName: string;
  address: StoreAddress;
  companyAddress: StoreCompanyAddress;
  /** The store's customer id at the billing provider, created by a subscription purchase. */
  stripeBillingId?: string;
  phoneNumber: string;
  language: {
    id: number;
    name: string;
    locale: string;
  };
  settings: {
    [key: string]: any;
    ITEM_CUSTOMIZATION_FLOW?: ItemCustomizationFlow | string;
    EAT_IN_EXTERNAL_ID?: string;
    TAKEAWAY_EXTERNAL_ID?: string;
    CATALOG_CATEGORY_FILTER_MODE?: boolean;
    TOP_LEVEL_CATEGORIES_VIEW?: 'DISABLED' | 'ENABLED';
    CATEGORY_MENU_STYLE?: 'LARGE_PICTURES' | 'COMPACT';
  };
  externalId: string;
  numberOfLocations: number;
  numberOfOffers: number;
  numberOfOrders: number;
  hasOrders?: boolean;
  deleted?: boolean;
  deletedAt?: string;
  subscription?: StoreSubscription;
  currency?: {
    name: string;
    isoCode: string;
    iso4217Code: string;
    symbol: string;
    id: number;
  };
  timeZone?: string;
  orderMinAmountDelivery?: number;
  orderFeeDelivery?: number;
  orderAmountFreeDelivery?: number;
  // True when the store has a driving-distance delivery zone — the only zone
  // type that needs the address geocoded to be evaluated. See
  // isGeolocationRequired() in checkout.service.
  hasDrivingDistanceZone?: boolean;
  specialSchedules: SpecialSchedule[];
  tag: string;
  createdAt: string;
  vatPercentage?: number;
  catalogId?: number;
  relation: Relation;
  isIndependent?: boolean;
  locationUrl?: string;
  openTimeSchedule?: {};
  platformOrderFee?: number;
  platformOrderPercentageFee?: number;
  platformPaymentFee?: number;
  platformPaymentPercentageFee?: number;
  platformPaymentProviderMarkupFee?: number;
  platformPaymentProviderMarkupPercentageFee?: number;
  subscriptionPrice1Month?: number;
  subscriptionPrice1Year?: number;
  crmCompanyId?: number;
  tenant?: Tenant;
  firstLoginVoucherId?: number;
  locale?: string;
  providerConnectUrl?: string;
  selectedCurrency?: {
    name: string;
    isoCode: string;
    iso4217Code: string;
    symbol: string;
    id: number;
    locale?: string;
  };
  activePaymentMethods?: PaymentMethod[];
  promotion?: {
    id: number;
    imageUrl?: string;
    name: string;
    shortDescription?: string;
    longDescription?: string;
  };
  salesPointResolution?: SalesPointResolution;
  storeTier?: StoreTier | null;
  parentStoreId?: number | null;
}

export interface SalesPointResolution {
  resolved: boolean;
  salesPointId?: number;
  name?: string;
}

export interface StoreAddress {
  addressLine1: string;
  addressLine2: string;
  postCode: string;
  region: string;
  city: string;
  country: {
    id: number;
    name: string;
    code?: string;
    phoneCode: string;
    defaultLocale?: string;
    europeanCountry: boolean;
    subscriptionCurrency?: string;
  };
}
export interface StoreCompanyAddress {
  addressLine1: string;
  addressLine2: string;
  postCode: string;
  region: string;
  city: string;
  country: {
    id: number;
    name: string;
    code?: string;
    phoneCode: string;
    defaultLocale?: string;
    europeanCountry: boolean;
  };
}

export interface ClientStoreRequest {
  id?: number;
  name?: string;
  description?: string;
  aliasName?: string;
  externalId?: string;
  longitude?: number;
  latitude?: number;
  countryId?: number;
  languageId?: number;
  addressLine1?: string;
  addressLine2?: string;
  postCode?: string;
  region?: string;
  city?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
  timeZone?: string;
  orderMinAmountDelivery?: number;
  orderFeeDelivery?: number;
  subscription?: StoreSubscription;
  crmCompanyId?: string;
  firstLoginVoucherId?: number | null;
  tenantCode?: string;
  settings?: { [key: string]: any };
  placeId?: string;
  addressSource?: string;
  selectedCurrency?: {
    name: string;
    isoCode: string;
    iso4217Code: string;
    symbol: string;
    id: number;
  };
  currency?: {
    name: string;
    isoCode: string;
    iso4217Code: string;
    symbol: string;
    id: number;
  };
  promoteCreationMode?: 'ADD_SALES_CHANNEL' | 'ADD_STORE_TO_ORGANIZATION';
  sourceStoreId?: number;
}

export interface StoreSettingUpdateRequest {
  key: string;
  value: any;
}

export interface QRCodeResponse {
  blob: Blob;
  filename: string;
}

export interface OrderItemXlsResponse {
  blob: Blob;
  filename: string;
}
export type StoreLastUpdateType = 'ORDER' | 'CATALOG' | 'STORE_SETTINGS';

export interface StoreLastUpdateResponse {
  storeId: number;
  updateType: StoreLastUpdateType;
  lastUpdatedAt: string | null;
}

export interface CategoriesListResponse {
  categories: Category[];
}

export interface Category {
  categoryId: number;
  name: string;
  shortDescription?: string;
  longDescription?: string;
  availability: boolean;
  hierarchyLevel?: string;
  isSellable: boolean;
  languageTranslation?: LanguageTranslation[];
  max?: number;
  min: number;
  offers: Offer[];
  position: number;
  catalogId: number;
  orderMultipleSameItem?: boolean;
  topLevelCategoryId?: number;
  standardImage?: string;
}

export interface StoreCatalog {
  categories: Category[];
  topLevelCategories?: Category[];
  offers: Offer[];
  catalogId: number;
  currency: string;
  parentStoreId?: number;
}

export interface SaveOfferView {
  offer: Offer;
  contentItem: ContentItemModel[];
}

export interface User {
  id: number;
  email: string;
  username: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  phoneCode: string;
}

export interface SaveCategoryView {
  category: Category;
  contentItem: ContentItemModel[];
}

export interface SaveOfferPositionView {
  offers: Offer[];
}
export interface SaveCategoryRequest {
  category: Category;
  contentItem: ContentItemModel[];
}

export interface Offer {
  offerId: number;
  name: string;
  shortDescription?: string;
  longDescription?: string;
  price: number;
  discount: number;
  categoryId: number;
  position: number;
  availability: number;
  contentItem?: ContentItemModel[];
  discountType: string;
  aliasName?: string;
  isSellable: boolean;
  global?: boolean;
  catalogId: number;
  standardImage?: ImageData; // needed to be (at least optionally) defined for public interface
  options?: any[]; // needed to be (at least optionally) defined for public interface
  categories?: Category[];
  contentItemId?: number;
  hierarchyLevel?: string;
  languageTranslation?: LanguageTranslation[];
  variants?: OfferVariant[];
  priceDescription?: string;
  isOrderable: boolean;
  attributeDtos: KeyValuePair[];
  isDirectlyOrderable?: boolean;
  isExpandable?: boolean;
  inStock?: boolean;
  originalOffer?: Offer;
  consumption?: number;
  itemType?: string;
  formattedFromPrice?: string;
  // Icon of the item as the first choice of its price variants (#11754).
  variantIcon?: string;
  // Item tags in the catalog sequence, label in the customer's language (#11750).
  tags?: ItemTagDisplay[];
  /** #11751: present on a product response only when the upsell suggestion can show. */
  upsell?: OfferUpsell;
}

/** The product a store suggests instead of the one the customer opened (#11751). */
export interface OfferUpsell {
  offerId: number;
  /** Translated in the request language. */
  name: string;
  imageUrl: string | null;
  /** Effective (discounted) price of the upsell product. */
  price: number;
}

export interface OfferVariant {
  offerId: number;
  price: number;
  priceDescription?: string;
  discount?: number;
  discountType?: string;
  categories?: Category[];
  consumption?: number;
  attributeDtos?: KeyValuePair[];
  variantIcon?: string;
}

export interface ContentItemModel {
  languageId: number;
  name: string;
  shortDescription: string;
  longDescription: string;
}

export interface GiftCardResponse {
  id?: number;
  code?: string;
}

export interface OrderItem {
  childItemRequests?: any[];
  offerId: number;
  categoryId?: number;
  name?: string;
  hierarchyLevel?: string;
  variantOfferId?: number;
  priceDescription?: string;
  itemName?: string;
  quantity: number;
  discountType?: string;
  discountValue?: number;
  offerPrice?: number;
  discountedOfferPrice?: number;
  price?: number;
  totalNonDiscountedPrice?: number;
  totalDiscountedPrice?: number;
  uuid?: string;
  comment?: string;
  formattedDiscountValue?: string;
  formattedOfferPrice?: string;
  formattedTotalNonDiscountedPrice?: string;
  formattedTotalDiscountedPrice?: string;
  childOrderItems?: OrderItem[];
  wishTime?: string;
  itemType?: string;
  customerAddress?: CustomerAddress;
  // Gift-card credit value (the store credit the customer receives for the cash they pay).
  // Populated by the backend's OrderItemEntityToDtoMapper from the catalog offer / variant. The
  // basket / checkout render the "price → credit" arrow when this differs from offerPrice
  // (#9862).
  consumption?: number;
  giftCardResponses?: GiftCardResponse[];
  // "Count against slot count" of the line's offer as the backend resolves it
  // (#9840): on a line the price variant's value is folded in, on a child it is
  // the option's own. Null when the offer has no such attribute; absent from
  // backends that do not send it yet.
  countAgainstSlot?: number | null;
  // The line's catalog offer, as far as the customer UI reads it: a
  // reservable line cannot go above one (#11979).
  offer?: Pick<Offer, 'itemType'>;
}

export interface Order {
  locationId?: number;
  location?: string | number;
  locationComment?: string;
  orderItems?: OrderItem[];
  status?:
    | 'DRAFT'
    | 'SUBMITTED'
    | 'RECEIVED'
    | 'CLOSED'
    | 'CANCELLED'
    | 'REJECTED'
    | 'PENDING_CONFIRMATION'
    | 'PENDING_CUSTOMER_PICK'
    | 'MARKETPLACE_FULFILLED';
  uuid?: string;
  totalDiscountedPrice?: number;
  totalNonDiscountedPrice?: number;
  deliveryFee?: number;
  storeZoneId?: number;
  storeZoneName?: string;
  showDeliveryZoneNameNextToFee?: boolean;
  transactionFee?: number;
  formattedTotalDiscountedPrice?: string;
  formattedTotalNonDiscountedPrice?: string;
  formattedDeliveryFee?: string;
  formattedTransactionFee?: string;
  formattedServiceChargeAmount?: string;
  comment?: string;
  customerUserId?: number;
  customerName?: string;
  customerPhoneNumber?: string;
  customerEmail?: string;
  buzzerNumber?: string;
  deliveryMethod?: 'NO_LOCATION' | 'IN_STORE_LOCATION' | 'ADDRESS';
  consumptionMode?: 'EAT_IN' | 'TAKE_AWAY';
  paymentStatus?:
    | 'NO_PAYMENT'
    | 'IN_PROGRESS'
    | 'SUCCESSFULLY_COMPLETED'
    | 'FAILED'
    | 'PAID_OFFLINE'
    | 'PAID_CARD';
  isPaid?: boolean;
  currency?: string;
  orderToken?: string;
  createdAt?: string;
  updatedAt?: string;
  wishTime?: string;
  slotEndTime?: string;
  estimatedDuration?: Date;
  isReady?: boolean;
  deliveryStreetAddress?: string;
  deliveryPostCode?: string;
  deliveryCity?: string;
  pickupStreetAddress?: string;
  pickupPostCode?: string;
  pickupCity?: string;
  deliveryHouseNo?: string;
  deliveryFeeApply?: boolean;
  floorNumber?: string;
  doorbell?: string;
  pickupFloorNumber?: string;
  pickupDoorbell?: string;
  estimatedTime?: string;
  rejectReason?: string;
  uiLanguageLocale?: string;
  catalogLanguageLocale?: string;
  voucherCode?: string;
  voucherDiscount?: number;
  voucherDiscountType?: 'MONETARY' | 'PERCENTILE';
  voucherDiscountPercentage?: number;
  formattedVoucherDiscount?: string;
  latitude?: number;
  longitude?: number;
  pickupLatitude?: number;
  pickupLongitude?: number;
  storeId?: number;
  storeAliasName?: string;
  /** Display name of the store the order was placed at, as opposed to the URL slug above. */
  storeName?: string;
  storeUserFirstName?: string;
  storeUserLastName?: string;
  paymentMethod?: string;
  tapId?: number;
  external_address_delivery_id?: number;
  externalAddressDeliveryNumber?: string;
  externalOrderId?: string;
  submittedAt?: string;
  deliveryComment?: string;
  currencyIsoCode?: string;
  originType?: string;
  originDomain?: string;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  deliveryServiceChargePercentage?: number;
  serviceChargeCalculationMethod?: 'PERCENTAGE' | 'MINIMUM';
  billingStreetAddress?: string;
  billingHouseNo?: string;
  billingPostCode?: string;
  billingCity?: string;
  billingState?: string;
  billingCountryId?: number;
  billingCountryName?: string;
  billingVatNumber?: string;
  posTechnicalIssueFlag?: boolean;
  preferredMode?: string;
  returnDate?: string;
  paymentTransaction?: PaymentTransaction;
  tip?: number;
  formattedTip?: string;
  refund?: Refund;
  printerName?: string;
  printerId?: number;
  printerExternalId?: string;
  // Refund metadata from backend
  refundType?: string;
  formattedTotalVatAmount?: string;
  totalVatAmount?: number;
  validated?: boolean;
  tableOrder?: boolean;
  itemsSubtotalPrice?: number;
  formattedItemsSubtotalPrice?: string;
  // New delivery tracking fields
  isBeingDelivered?: boolean;
  isCompleted?: boolean;
  deliveryProvider?: 'WOLT_DRIVE' | 'FLEX_DELIVERY' | 'MYNEXT' | 'WOODELIVERY';
  deliveryStatus?:
    | 'REQUESTED'
    | 'ACCEPTED'
    | 'PICKUP_EN_ROUTE'
    | 'PICKUP_ARRIVED'
    | 'PICKED_UP'
    | 'DROPOFF_EN_ROUTE'
    | 'DROPOFF_ARRIVED'
    | 'DELIVERED'
    | 'CANCELLED'
    | 'FAILED'
    | 'READY_FOR_PICKUP'
    | 'INFO_RECEIVED';
  deliveryStatusUpdatedAt?: string;
  deliveryTrackingId?: string;
  deliveryTrackingUrl?: string;
  deliveryPickupEtaTimestamp?: string;
  deliveryDropoffEtaMinTimestamp?: string;
  deliveryDropoffEtaMaxTimestamp?: string;
  deliveryCancelReason?: string;
  deliveryCourierVehicleType?: string;
  properties?: OrderProperties;
  // Order Decomposition fields
  decompositionRole?: 'NONE' | 'PARENT' | 'CHILD';
  parentOrderUuid?: string;
  parentOrderToken?: string;
  parentOrderStoreId?: number;
  childOrders?: ChildOrderSummary[];
  daySequence?: number;
  invoice?: OrderInvoice;
  salesPointId?: number;
  salesPointName?: string;
  salesPointExternalId?: string;
  /** Card #11230 — set only by getInheritedPendingOrders(); the single source of
   * truth for "this order came from the child-store inherited endpoint". */
  isInherited?: boolean;
  /** Card #11767 — the device id the app sends on the order-creation request.
   * Request-only: no order endpoint returns it. */
  deviceIdentifier?: string;
}

export interface OrderProperties {
  CONSUMPTION_MODE?: 'EAT_IN' | 'TAKE_AWAY';
  PREVIOUS_ORDER_ID?: string;
}

export interface ChildOrderSummary {
  uuid: string;
  orderToken?: string;
  wishTime?: string;
  status?:
    | 'DRAFT'
    | 'SUBMITTED'
    | 'RECEIVED'
    | 'CLOSED'
    | 'CANCELLED'
    | 'REJECTED'
    | 'PENDING_CONFIRMATION'
    | 'PENDING_CUSTOMER_PICK'
    | 'MARKETPLACE_FULFILLED';
  isReady?: boolean;
  isCompleted?: boolean;
  daySequence?: number;
  itemCount?: number;
  totalDiscountedPrice?: number;
  storeId?: number;
}

export interface OrderInvoice {
  id?: number;
  provider?: string;
  externalId?: string;
  series?: string;
  number?: string;
  invoiceUrl?: string;
  status?: string;
}

export interface Refund {
  id?: number;
  refundDate?: string;
  refundAmount?: number;
  refundMethod?: string;
  orderToken?: string;
  orderAmount?: number;
  reason?: string;
}
export interface ReorderResponse {
  order: Order;
  reorderedItems?: OrderItem[];
  unavaliableItems?: OrderItem[];
}

export interface PaymentTransaction {
  id?: string;
  paymentMethod?: string;
  paymentProviderIdentifier?: string;
  amount?: number;
  currency?: Currency;
  referenceType?: ReferenceType;
  paymentStatus?:
    | 'NO_PAYMENT'
    | 'DRAFT'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'FAILED'
    | 'ERROR'
    | 'PAID_OFFLINE'
    | 'PAID_CARD';
  paymentParams?: any;
}

export interface StoreViewState {
  state:
    | 'LOADING'
    | 'LOADED'
    | 'VIEWPRODUCTDETAILS'
    | 'VIEWPRODUCTDETAILSFROMCARTVIEW'
    | 'LOGIN'
    | 'PROFILE'
    | 'EDITPROFILE'
    | 'EDITADDRESS'
    | 'PRECHECKOUT'
    | 'VIEWCART'
    | 'CHECKOUT'
    | 'CHECKOUT_RECOMMENDATIONS'
    | 'THANKYOUPAGE'
    | 'ERRORPAGE'
    | 'EMPTYCARTPAGE'
    | 'STANDALONEPAYMENT'
    | 'PAYMENTPROGRESS'
    | 'PAYMENTPAY'
    | 'QRCODEPAY'
    | 'REQUIREDLOCATION'
    | 'SALESPOINTREQUIRED'
    | 'KIOSKVIDEO'
    | 'CONSUMPTION_MODE'
    | 'PROMOTION'
    | 'PAYMENTPROGRESSERROR'
    | 'CONFIRMATIONFAILEDPAGE'
    | 'STORES'
    | 'LOYALTY_PROMOTIONS'
    | 'MEMBER_CODE'
    | 'BROCHURE';
}

export interface OrderMeta {
  comment?: string;
  customerUserId?: number;
  customerEmail?: string;
  customerName?: string;
  customerPhoneNumber?: string;
  customerStreet?: string;
  customerZip?: string;
  customerTown?: string;
  placeId?: string;
  addressSource?: string;
  // Client-side geocode correlation tokens. Never mapped onto
  // OrderUpdateRequest and never persisted to the cookie — see
  // address-identity.helper.ts.
  addressId?: string;
  pickupAddressId?: string;
  pickupStreet?: string;
  pickupZip?: string;
  pickupTown?: string;
  pickupPlaceId?: string;
  pickupAddressSource?: string;
  deliveryHouseNo?: string;
  deliveryMethod?: string;
  paymentOption?: number;
  paymentMethod?: number;
  paymentStatus?: string;
  location?: string | number;
  deviceIdentifier?: string;
  voucherCode?: string;
  wishTime?: string;
  customerFloor?: string;
  doorbell?: string;
  pickupFloor?: string;
  pickupDoorbell?: string;
  latitude?: number;
  longitude?: number;
  pickupLatitude?: number;
  pickupLongitude?: number;
  tapId?: string;
  externalOrderId?: string;
  deliveryComment?: string;
  billingStreetAddress?: string;
  billingHouseNo?: string;
  billingPostCode?: string;
  billingCity?: string;
  billingState?: string;
  billingCountryId?: number;
  billingVatNumber?: string;
  buzzerNumber?: number;
  selectedAddressId?: number;
  tip?: number;
  preferredMode?: string;
  CONSUMPTION_MODE?: 'EAT_IN' | 'TAKE_AWAY';
  PREVIOUS_ORDER_ID?: string;
  previousOrderLocationLabel?: string;
  previousOrderLocationDescription?: string;
  paymentCategory?: string | null;
  adminVoucherOutcome?: string | null;
  adminPaymentVoucherCode?: string | null;
}

export interface Availability {
  id: number;
  startTime: string;
  endTime: string;
}

export interface Lang {
  id: number;
  locale: string;
  name: string;
}

export interface LanguageTranslation {
  languageId: number;
  name: string;
  shortDescription?: string;
  longDescription?: string;
}

export interface Voucher {
  id: number;
  code: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  maxTimesUse: number;
  value: number;
  discount: number;
  owner: number;
  commission: number;
  comment: string;
  voucherAssignmentStatus: string;
  formattedDiscountAmount: string;
  formattedCommissionAmount: string;
}

export interface Commission {
  code: string;
  storeId: number;
  storeAlias: string;
  commission: number;
  commissionAmount: number;
  formattedCommissionAmount: string;
  useDate: string;
  formattedEndDate: string;
  commissionStatus: string;
}

export interface StoreVoucherRequest {
  id: number;
  total: number;
  discount: number;
  commission: number;
  comment: string;
  mode: string;
  code: string;
  endDate: any;
  voucherAssignmentStatus: string;
  formattedDiscountAmount: string;
  formattedCommissionAmount: string;
}

export interface StoreStatistics {
  day: number;
  year: number;
  month: number;
  draftCount: number;
  submitCount: number;
  receivedCount: number;
  cancelCount: number;
  closedCount: number;
  totalSubmitted: number;
  totalViewings: number;
  totalPaid: number;
  totalNonPaid: number;
  total: number;
  totalTips: number;
}

export interface LoyaltyStatistics {
  groupBy: string; // 'STORE' | 'PERIOD'
  totals: LoyaltyTotals;
  rows: LoyaltyRow[];
}

export interface LoyaltyTotals {
  amountPaid: number;
  pointsAwardedTotal: number;
  pointsAwardedFromOrders?: number | null;
  pointsAwardedFromAdmins?: number | null;
  pointsRedeemedTotal: number;
  rewardCollected: number;
  amountRedeemed: number;
  loyaltyOrders: number;
}

export interface LoyaltyRow {
  storeId?: number;
  storeName?: string;
  bucketStart?: string;
  amountPaid: number;
  pointsAwardedTotal: number;
  pointsAwardedFromOrders?: number | null;
  pointsAwardedFromAdmins?: number | null;
  pointsRedeemedTotal: number;
  rewardCollected: number;
  amountRedeemed: number;
  loyaltyOrders: number;
}

export interface StoreOrderItemsStatistics {
  offer: string;
  totalQuantity: number;
  totalNonDiscountedPrice: number;
  totalDiscountedPrice: number;
  imageUrl: string;
}

export interface NotificationStatRow {
  date: string;
  messages: number;
}

export interface NotificationStatisticsResponse {
  period: 'DAILY' | 'MONTHLY';
  storeId: number;
  records: NotificationStatRow[];
}

export enum LocationType {
  TABLE = 'TABLE',
  ROOM = 'ROOM',
  LOCATION = 'LOCATION',
  POLE = 'POLE',
  SEAT = 'SEAT',
  SUNBED = 'SUNBED',
  UMBRELLA = 'UMBRELLA',
  SOFA = 'SOFA',
  AIRBNB = 'AIRBNB',
}

export interface LocationValid {
  isValid: boolean;
  id?: number;
  label?: string;
  storeLocations?: StoreLocation[];
  locationType?: LocationType;
  description?: string;
  status?: string;
  deliveryFeeApply?: boolean;
  minOrderAmount?: number;
  openTab?: boolean;
}

export interface StoreLocation {
  id: number;
  label: string;
}

export interface TimeZone {
  zoneId: string;
  label: string;
}

export interface EmailMessage {
  body: {};
  statusCode: string;
  statusCodeValue: number;
}

export interface SendEmailModel {
  storeId: number;
  userId: number;
  message: string;
}

export interface StoreZone {
  id?: number;
  name: string;
  type: string;
  minRadius?: number;
  maxRadius?: number;
  radiusUnit: string;
  orderMinAmountDelivery?: number;
  orderFeeDelivery?: number;
  orderAmountFreeDelivery?: number;
  postcodes: string;
  settings: { [key: string]: any };
  scheduleId: number;
  position?: number;
}

export interface SaveZonePositionView {
  zones: StoreZone[];
}

export interface StoreZoneStatus {
  enableRestriction: boolean;
}

export type SpecialScheduleType = 'OPENING_HOURS';

export interface SpecialSchedule {
  id: number;
  type: SpecialScheduleType;
  schedule: Schedule;
}
export interface KeyValuePair {
  key: string;
  value: boolean;
}

export enum ItemCustomizationFlow {
  SINGLE_PAGE = 'SINGLE_PAGE',
  STEP_BY_STEP = 'STEP_BY_STEP',
}

export class MynextLoginResponse {
  ApiKey: string;
  MyNextId: string;
  Message: string;
}
export class PowersoftLoginResponse {
  accessToken: string;
  agentCode: string;
  genericCustomerCode: string;
  genericCustomerEmail: string;
}

export class PowersoftLoginRequest {
  accessToken: string;
  agentCode: string;
  genericCustomerCode: string;
  genericCustomerEmail: string;
}

export class WoltLoginRequest {
  venueId: string;
  username: string;
  password: string;
}

export interface Customer {
  id: any;
  name: string;
  email: string;
  phoneNumber: string;
  orderCount: number;
  firstOrderUpdatedAt: string;
  lastOrderUpdatedAt: string;
  reservationCount?: any;
  customerStatus?: string;
  loyalty: boolean;
  identified: boolean;
  registered: boolean;
  birthday?: Date;
  customerBirthday?: Date;
  dateOfBirthUpdatedAt?: Date;
}
export interface CustomerBasic {
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  orderCount?: number;
  streetAddress?: string;
  city?: string;
  zipCode?: string;
  customerStatus?: any;
  customerGender?: string;
  customerBirthday?: Date;
  customerPreferences?: any;
  instagramUsername?: string;
  addresses?: CustomerAddress[];
  externalCustomers?: ExternalCustomer[];
}

/** The customer's record in a POS at this store (#11125). */
export interface ExternalCustomer {
  provider: string;
  externalCustomerId: string;
}

export interface CustomersListDownloadResponse {
  blob: Blob;
  filename: string;
}

export interface AssociatedZone {
  id: number;
  name: string;
  type: string;
  orderMinAmountDelivery: number;
  orderFeeDelivery: number;
  orderAmountFreeDelivery: number;
  postcodes: string;
  settings: { [key: string]: any };
  storeId: number;
}
export type StoreTier = 'ORGANIZATION' | 'STORE' | 'SALES_CHANNEL';

export interface StoreRelations {
  isIndependent: boolean;
  storeAlias: string;
  storeId: number;
  storeName: string;
  /** Only populated on `Relation.allDescendantStores`, where the list is flat and this is what
   *  lets the client re-nest it. Absent on childStores / parentStore / siblingStores.
   *
   *  On `allDescendantStores` it is guaranteed, not best-effort: `StoreRelationEntityToDtoMapper`
   *  sets it on every row it emits, and the admin store payload `LoadStore` fetches
   *  (`GET /api/v1/stores/{id}` -> `StoreEntityToDtoMapper`) goes through that same mapper, so the
   *  post-refusal re-render sees the same shape as the initial screen load. */
  parentStoreId?: number;
  settings: { [key: string]: any };
  address?: StoreAddress;
  orderFeeDelivery?: number;
  hasDeliveryZoneRestriction?: boolean;
  storeZoneType?: string;
  storeTier?: StoreTier | null;
}

export interface Relation {
  childStores: StoreRelations[];
  parentStore: StoreRelations;
  siblingStores: StoreRelations[];
  allDescendantStores: StoreRelations[];
  /**
   * Descendants offered in the Orders list Store filter — every store the orders query can return
   * rows for, and nothing else. Deliberately not `allDescendantStores`, which is built for the
   * customer multi-store checkout and recurses without a depth limit (great-grandchildren the
   * orders query cannot match). Independent children are included; soft-deleted stores are not.
   */
  orderFilterStores?: StoreRelations[];
  isIndependent?: boolean;
}

export interface StoreRelationRequest {
  isIndependent: boolean;
  parentStoreId: number;
}

export interface CustomEndPointValidationRequest {
  endpoint?: string;
  validationEndpoint?: string;
  meganetEndpoint?: string;
}

export interface PresetOrderData {
  comment?: string;
  customerEmail?: string;
  customerName?: string;
  customerPhoneNumber?: string;
  deliveryStreetAddress?: string;
  deliveryPostCode?: string;
  deliveryCity?: string;
  pickupStreetAddress?: string;
  pickupPostCode?: string;
  pickupCity?: string;
  deliveryComment?: string;
  floorNumber?: string;
  doorbell?: string;
  latitude?: number;
  longitude?: number;
  pickupLatitude?: number;
  pickupLongitude?: number;
  pickupFloorNumber?: string;
  pickupDoorbell?: string;
  externalOrderId?: string;
  deliveryHouseNo?: string;
}
export enum StoreSaveMode {
  QRSCAN = 'QRSCAN',
  URL = 'URL',
  DEEPLINK = 'DEEPLINK',
}

export enum EmailStatus {
  UNVERIFIED = 'Unverified',
  VERIFIED = 'Verified',
  BOUNCED = 'Bounced',
}

export enum PhoneStatus {
  UNVERIFIED = 'Unverified',
  VERIFIED = 'Verified',
  BOUNCED = 'Bounced',
}

export interface StoreRecentsInfo {
  storeId: number;
  storeAlias: string;
  storeName: string;
  storeUrl: string;
  saveMode: StoreSaveMode;
  storeLogoUrl: string;
  lastOpen: string;
}
export interface CustomerLoyalty {
  balance: number;
  voucherValue: number;
  voucherCode: string;
  voucherQrCode: string;
  createdAt: string;
  updatedAt: string;
}
export interface CustomerForm {
  email?: string;
  firstName: string;
  lastName: string;
  countryId?: number;
  phoneNumber?: string;
  preferredLanguageId?: number;
  memberCode?: string;
  gender?: string;
  birthday?: string | null;
  instagramUsername?: string;
  tenantCode?: string;
  preferences?: Record<string, unknown>;
}
export interface CustomerDetail {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  profilePicture: string;
  emailVerified: EmailStatus;
  phoneVerified: PhoneStatus;
  phoneNumber: string;
  loginAt: string;
  memberCode?: string;
  country: {
    id: number;
    name: string;
    code: string;
    phoneCode: string;
    defaultLocale: string;
    defaultTimeZone: string;
    subscriptionPrice: number;
    subscriptionCurrency: string;
    europeanCountry: boolean;
  };
  preferredLanguage: {
    id: number;
    name: string;
    locale: string;
    code: string;
    covered_admin_ui: boolean;
    covered_customer_ui: boolean;
  };
  googleAccount?: {
    appleIdentifier?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    picture?: string;
  };
  facebookAccount?: {
    appleIdentifier?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    picture?: string;
  };
  appleAccount?: {
    appleIdentifier?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    picture?: string;
  };
  storeRole: string;
  roles: Array<string>;
  registeredAt?: string;
  instagramUsername?: string;
}

export interface CustomerOrderSummary {
  count: number;
  firstUpdated: string;
  lastUpdated: string;
}

export interface CustomerReservationStatistics {
  count: number;
  firstUpdated: string;
  lastUpdated: string;
}

export interface CustomerEventBookingsStatistics {
  bookingCount: number;
  firstBookingDate: string;
  lastBookingDate: string;
  ticketCount: number;
  reservationCount: number;
  totalSpent: number;
}

export interface CustomerOrderStatistics {
  registeredAt: string;
  lastLogindAt: string;
  accountType: Array<any>;
}

export interface CustomerLoyaltyProfile {
  balance?: number;
  voucherValue?: number;
  totalDiscountedAmountSpent?: number;
  totalNonDiscountedAmountSpent?: number;
  totalRewardCollected?: number;
  firstRegisteredStoreId?: number;
  firstRegisteredStoreName?: string;
  amountCollected?: number;
  totalCollected?: number;
  totalRedeemed?: number;
  amountRedeemed?: number;
  voucherCode?: string;
  lastTransaction?: any;
  sinceMemebership?: any;
  updatedAt?: any;
  createdAt?: any;
}
export interface CustomerLoyaltyProfileUpdate {
  balance?: number;
  voucherValue?: number;
  totalNonDiscountedAmountSpent?: number;
  totalCollected?: number;
  totalRedeemed?: number;
}

export interface CustomerLoyaltyProfileUpdateData {
  amountToRedeem: number;
  amountCollected: number;
}

export interface CustomerOrderItem {
  offerId: number;
  offer: string;
  totalQuantity: number;
  totalDiscountedPrice: number;
  imageUrl?: string;
}
export interface CustomerRecentOrders {
  content: Array<any>;
}
export interface CustomerOrder {
  orderId?: number;
  uuid?: string;
  orderToken?: string;
  deliveryMethod?: string;
  customerName?: string;
  amountPaidOnline?: number;
  wishTime?: string;
  createdAt?: string;
  submittedAt?: string;
  updatedAt?: string;
  estimatedTime?: string;
}

export interface CustomerOrderItems {
  data: Array<CustomerOrderItem>;
}

export interface CustomerAssignedVoucher {
  id: number;
  code: string;
  discount: number;
  formattedDiscount?: string;
  discountType: string;
  type?: string;
  storeId: number;
  isActive: boolean;
  isConsumed: boolean;
  startDate?: string;
  endDate?: string;
  createdAt?: string;
  updatedAt?: string;
  initialValue?: number;
  orderMinAmount?: number;
  formattedOrderMinAmount?: string;
}

export interface CustomerLoyaltyTrasaction {
  orderToken: string;
  nonDiscountedAmountSpent: number;
  initialBalance: number;
  finalBalance: number;
  initialReward: number;
  amountCollected: number;
  amountRedeemed: number;
  finalReward: number;
  createdAt: string;
  storeId: number;
  uuid: string;
}

export interface CustomerLoyaltyTrasactions {
  content: Array<CustomerLoyaltyTrasaction>;
}

export interface CustomerReservation {
  createdAt: string;
  updatedAt: string;
  id: number;
  uuid: string;
  storeId: number;
  reservationNumber: number;
  arrivalTime: string;
  departureTime: string;
  numberOfPeople: number;
  name: string;
  phone: string;
  email: string;
  status: string;
  comment: string;
  occasion: string;
  reason: string;
  table: string;
}

export interface UploadResponse {
  downloadUrl: string;
}

export enum StoreIntegrationType {
  PAYMENT_PROVIDERS,
  POS,
}

export enum StoreIntegrationsPosTypes {
  MYNEXT = 'mynext',
  FLEXDELIVERY = 'flexdelivery',
  INSTADELIVERY = 'instadelivery',
  SUNSOFT = 'sunsoft',
  TWINSOFT = 'twinsoft',
  HIT = 'hit',
  POWERSOFT = 'powersoft',
  EPOSNOW = 'eposnow',
  HUBRISE = 'hubrise',
  SHOPIFY = 'shopify',
  POSTER = 'poster',
  EPOSDIRECT = 'eposdirect',
  CUSTOM = 'custom',
  WOLT = 'wolt',
  IOTSOFT = 'iotsoft',
  WITEC = 'witec',
  MEGANET = 'meganet',
  WPOS = 'wpos',
  PURPLEI = 'purplei',
  TISSL = 'tissl',
  LOYVERSE = 'loyverse',
  GLADIUS = 'gladius',
}

export enum StoreIntegrationsPaymentProvidersTypes {
  PAYABL = 'payabl',
  MOLLIE = 'mollie',
  JCC = 'jcc',
  STRIPE = 'stripe',
  TRUST = 'trust',
  GLOBALPAY = 'globalpay',
  RMS = 'rms',
  PAYMENTSENSE = 'paymentsense',
  VIVA = 'viva',
  EPAY = 'epay',
  SQUARE = 'square',
  FYGARO = 'fygaro',
  APCOPAY = 'apcopay',
  HOBEX = 'hobex',
  RYFT = 'ryft',
  ABAPAYWAY = 'abapayway',
  ROOTLINE = 'rootline',
  SOLIDGATE = 'solidgate',
  EMERCHANTPAY = 'emerchantpay',
  OTHER = 'other',
  PAYPAL = 'paypal',
  NEOSOFT = 'neosoft',
  FLITT = 'flitt',
  NBGPAY = 'nbgpay',
  CARDLINK = 'cardlink',
  EUROBANK = 'eurobank',
  ALPHABANK = 'alphabank',
  CITYPAY = 'citypay',
  VRPAYMENT = 'vrpayment',
  WALLEE = 'wallee',
  CLEARACCEPT = 'clearaccept',
  MYPOS = 'mypos',
  PXP = 'pxp',
  DNAPAYMENTS = 'dnapayments',
}

export interface Rule {
  actions: RuleAction[];
  conditions: RuleCondition[];
}

export interface RuleAction {
  data: ActionData;
}

export interface ActionData {
  categoryId: number;
  min: number;
  max: number;
  name: string;
  offers: Offer[];
  orderMultipleSameItem: boolean;
}

export interface RuleCondition {
  data: ConditionData[];
  type: string;
}

export interface ConditionData {
  min: number;
  max: number;
}

export interface StoreBalancePayload {
  openingBalanceCash: number;
  openingBalanceCard: number;
  openingBalanceOnline: number;
  actualClosingCash: number;
  actualClosingCard: number;
  actualClosingOnline: number;
  note: string;
}

// Promotion interfaces
export interface PromotionListResponse {
  data: PromotionListItem[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export interface PromotionListItem {
  id: number;
  imageUrl?: string;
  title: string;
  active: boolean;
  startDatetime: string;
  endDatetime: string;
  createdAt: string;
  updatedAt: string;
}

export interface PromotionResponse {
  id: number;
  active: boolean;
  imageUrl?: string;
  startDatetime: string;
  endDatetime: string;
  schedule?: {
    id: number;
    name: string;
    availabilities: Array<{
      dayOfWeek: string;
      startTime: string;
      endTime: string;
    }>;
  };
  notificationDatetime?: string;
  translations: {
    [languageCode: string]: {
      name: string;
      shortDescription?: string;
      longDescription: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface PromotionRequest {
  active: boolean;
  imageUrl?: string;
  startDatetime: string;
  endDatetime: string;
  scheduleId?: number;
  notificationDatetime?: string;
  translations: {
    [languageCode: string]: {
      name: string;
      shortDescription?: string;
      longDescription: string;
    };
  };
}

export interface Product {
  productId?: number;
  storeId?: number;
  catalogId?: number;
  name: string;
  shortDescription?: string;
  longDescription?: string;
  imageUrl?: string;
  standardImage?: string;
  price: number;
  vatPercentage?: number;
  externalProductId?: string;
  vatExternalId?: string;
  isActive: boolean;
  attributeDtos?: KeyValuePair[];
  languageTranslation?: LanguageTranslation[];
  contentItem?: ContentItemModel[];
  contentItemId?: number;
  allergens?: string[];
  nutrients?: Nutrient[];
  characteristics?: KeyValuePair[];
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Product API Response Interfaces
 * These interfaces match the backend API response structures
 */

/**
 * Response for Load Products API (GET /api/v1/stores/{storeId}/catalog/{catalogId}/products)
 * Returns minimal product data for listing purposes
 */
export interface ProductListResponse {
  productId: number;
  name: string;
  price: number;
  standardImage?: string;
}

/**
 * Response for Load Linkable Products API (GET /api/v1/stores/{storeId}/catalog/{catalogId}/products/linkable)
 * Returns product data for searching and linking products between catalogs
 */
export interface ProductSearchResponse {
  productId: number;
  name: string;
  externalProductId?: string;
  price: number;
  isActive: boolean;
  standardImage?: string;
  storeId: number;
  catalogId: number;
  shortDescription?: string;
  vatPercentage?: number;
}

/**
 * Enhanced language translation interface for products
 * Extends base LanguageTranslation with product-specific fields
 */
export interface ProductLanguageTranslation extends LanguageTranslation {
  priceDescription?: string;
}

/**
 * Response for Get/Create/Update Product APIs
 * Returns full product details including translations and attributes
 */
export interface ProductResponse {
  productId: number;
  name: string;
  shortDescription?: string;
  longDescription?: string;
  attributeDtos: KeyValuePair[];
  price: number;
  externalProductId?: string;
  vatPercentage?: number;
  vatExternalId?: string;
  isActive: boolean;
  standardImage?: string;
  contentItemId?: number;
  languageTranslation?: ProductLanguageTranslation[];
  storeId: number;
}

/**
 * Request body for Create/Update Product APIs
 * Used when creating or updating products
 */
export interface ProductRequest {
  name: string;
  shortDescription?: string;
  longDescription?: string; // max 2000 chars
  attributeDtos?: KeyValuePair[];
  price: number;
  externalProductId?: string;
  vatPercentage?: number;
  vatExternalId?: string;
  isActive: boolean;
  imageUrl?: string;
  calories?: number;
  protein?: number;
  netCarbs?: number;
  totalFats?: number;
}

/**
 * Response for Product Import/Export APIs
 * Tracks the status of catalog import operations
 */
export interface CatalogImportResponse {
  id: number;
  startAt: string;
  finishAt?: string | null;
  fileName: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  catalogData?: any; // StoreCatalog or similar
  storeId: number;
  catalogId: number;
  type: 'PRODUCT_EXCEL' | 'CATALOG_EXCEL' | 'OPTION_GROUP_EXCEL';
}
